import { Fragment, FunctionComponent, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ConfigContext } from '../../configuration/ConfigContext';
import SimpleCheckBox from '../../forms/SimpleCheckBox';
import { usePropertyAssociationContext } from '../../propertyAssociation/PropertyAssociationContext';
import { updatePropertyAssociationSettings } from '../../settings/settingsService';
import Pagination from '../../shared/Pagination';
import { SortDirection, Table, TableHead, TableHeader } from '../../shared/Table';
import { usePagination } from '../../shared/usePagination';
import { MembersSortOrder } from '../memberConstants';
import { sortMembers } from '../membersSort';
import MemberDetailsModal from '../modals/MemberDetailsModal';
import { GetMembersResponse, Member } from '../models/Member';
import MemberItem from './MemberItem';

type Props = {
    defaultSortDirection: SortDirection;
    defaultSortOrder: MembersSortOrder;
    isPending: boolean;
    membersResponse: GetMembersResponse | undefined;
    isLoading: boolean;
    error: any;
    refetch: () => void;
};

const MemberList: FunctionComponent<Props> = ({ defaultSortDirection, defaultSortOrder, isPending, membersResponse, refetch }) => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const { currentPropertyAssociationId } = usePropertyAssociationContext();
    const navigate = useNavigate();
    const { search, pathname } = useLocation();
    const [selectedMember, setSelectedMember] = useState<Member | undefined>();
    const [sortOrder, setSortOrder] = useState(defaultSortOrder);
    const [sortDirection, setSortDirection] = useState(defaultSortDirection);
    const [sortedMembers, setSortedMembers] = useState<Member[]>([]);

    const emptyFunction = useCallback(() => {
        /* empty */
    }, []);

    useEffect(() => {
        const membersSorted = sortMembers(membersResponse?.members || [], sortOrder);
        if (sortDirection === 'descending') {
            membersSorted.reverse();
        }
        setSortedMembers(membersSorted);
    }, [membersResponse, sortOrder, sortDirection]);

    const { currentPage, setCurrentPage, currentItems: currentMembers, totalPages, itemsPerPage } = usePagination(sortedMembers);

    useEffect(() => {
        if (!search || !membersResponse?.members) {
            return;
        }

        const userId = new URLSearchParams(search).get('userId');
        const member = membersResponse.members.find((x) => x.user?.userId === userId);
        if (member) {
            setSelectedMember(member);
        }
    }, [search, membersResponse?.members]);

    const deselectMember = () => {
        navigate({ pathname }, { replace: true });
        setSelectedMember(undefined);
    };

    const showMemberDetails = (member: Member) => {
        const params = new URLSearchParams({ userId: member.user?.userId || '' });
        navigate({ pathname, search: params.toString() }, { replace: true });
        setSelectedMember(member);
    };

    const sortItems = (sortBy: MembersSortOrder) => {
        if (sortBy !== sortOrder) {
            setSortDirection('ascending');
            setSortOrder(sortBy);
            return;
        }

        if (sortDirection === 'ascending') {
            setSortDirection('descending');
            return;
        }

        if (sortDirection === 'descending') {
            setSortDirection('none');
            return;
        }

        setSortDirection('ascending');
    };

    const getSortDirection = (sortBy: MembersSortOrder): SortDirection => {
        if (sortBy !== sortOrder) {
            return 'none';
        }
        return sortDirection;
    };

    const toggleRequireMembership = () => {
        const requireMembership = !(membersResponse?.requireMembership ?? false);
        updatePropertyAssociationSettings(apiBaseUrl, currentPropertyAssociationId, { requireMembership }, refetch, emptyFunction);
    };

    return (
        <Fragment>
            <SimpleCheckBox
                name="requireMembership"
                label="Kräv manuellt hanterad åtkomst av folkbokförda användare för att dessa ska kunna boka resurser och ta del av anslagstavlan."
                isChecked={membersResponse?.requireMembership ?? false}
                onChange={toggleRequireMembership}
            />
            <Table withPagination={totalPages > 1}>
                <TableHead>
                    <tr>
                        <TableHeader onClick={() => sortItems(MembersSortOrder.FirstName)} sortDirection={getSortDirection(MembersSortOrder.FirstName)}>
                            Förnamn
                        </TableHeader>
                        <TableHeader onClick={() => sortItems(MembersSortOrder.LastName)} sortDirection={getSortDirection(MembersSortOrder.LastName)}>
                            Efternamn
                        </TableHeader>
                        <th></th>
                        <th className="d-none d-md-table-cell"></th>
                    </tr>
                </TableHead>
                <tbody>
                    {currentMembers.map((x) => (
                        <MemberItem key={x.user.userId} member={x} onClick={() => showMemberDetails(x)} requireMembership={membersResponse?.requireMembership ?? false} isPending={isPending} />
                    ))}
                </tbody>
            </Table>
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={sortedMembers.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                ariaLabel="Medlemslistans sidnavigering"
            />
            {selectedMember && <MemberDetailsModal onClose={deselectMember} member={selectedMember} onMemberUpdated={refetch} isPending={isPending} />}
        </Fragment>
    );
};

export default MemberList;
